import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SaleLinkDTO, SaleLinkService, SaleLinkStatus, SaleLinkType } from '../../services/sale-link.service';
import { SaleService } from '../../services/sale.service';
import { SaleDTO, SubscriptionDTO, ChargeDTO } from '../../core/models';
import { SubscriptionService } from '../../services/subscription.service';
import { SaleReadComponent } from '../sales/sale-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { formatApiDateOnly, parseApiCalendarDate } from '../../core/date-utils';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { CustomerService, CustomerDTO } from '../../services/customer.service';
import { ProductService, ProductDTO } from '../../services/product.service';
import { CustomerFormComponent } from '../customers/customer-form.component';
import { ProductFormComponent } from '../products/product-form.component';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { EditorModule } from 'primeng/editor';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';

type LabeledCustomer = CustomerDTO & { displayLabel: string };
type LabeledProduct = ProductDTO & { displayLabel: string };
type LineTaxView = {
  taxCode?: string;
  taxRate?: number;
  taxBase?: number;
  taxAmount?: number;
  collectionType?: string;
};
type SaleLinkFormLine = {
  product: LabeledProduct;
  quantity: number;
  description: string;
  unitPrice: number;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  taxes: LineTaxView[];
};

@Component({
  selector: 'app-sale-link-form',
  templateUrl: './sale-link-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-link-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-link-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-link-form-card.p-card .p-card-footer {
        padding: 0;
      }
      :host ::ng-deep .sale-link-form-card .p-tablist,
      :host ::ng-deep .sale-link-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-link-form-card .p-tabs-nav,
      :host ::ng-deep .sale-link-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .usage-select.p-select .p-select-label {
        padding-inline-end: 3.75rem;
      }
      :host ::ng-deep .sale-link-notes-editor .ql-editor,
      :host ::ng-deep .sale-link-notes-editor .ql-editor :is(p, li, ol, ul, span) {
        font-size: 1.125rem;
        line-height: 1.6;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header,
      :host ::ng-deep .subscription-details-drawer .p-drawer-header,
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content,
      :host ::ng-deep .subscription-details-drawer .p-drawer-content,
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-link-form-card .p-tabpanels,
      :host ::ng-deep .sale-link-form-card .p-tabpanel,
      :host ::ng-deep .sale-link-form-card .p-tabpanel-content,
      :host ::ng-deep .sale-link-form-card [data-pc-section='content'] {
        overflow: visible;
      }
    `
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    PERMISSION_UI_DIRECTIVES,
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    DialogModule,
    TooltipModule,
    CustomerFormComponent,
    ProductFormComponent,
    FloatLabelModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DatePickerModule,
    EditorModule,
    TabsModule,
    TagModule,
    DrawerModule,
    SaleReadComponent,
    SubscriptionReadComponent,
    CustomerReadComponent
  ]
})
export class SaleLinkFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private saleLinkService = inject(SaleLinkService);
  private saleService = inject(SaleService);
  private subscriptionService = inject(SubscriptionService);
  private customerService = inject(CustomerService);
  private productService = inject(ProductService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);

  private customerQuery = new Subject<string>();
  private productQuery = new Subject<string>();

  saving = signal(false);
  cancelling = signal(false);
  paymentUrl = signal<string | null>(null);
  paymentUrlLoading = signal(false);
  loading = signal(false);
  editMode = signal(false);
  editable = signal(true);
  linkNumber = signal<string | null>(null);
  linkStatus = signal<SaleLinkStatus | null>(null);
  customerEmail = signal('');
  sendDialogOpen = signal(false);
  sendEmail = signal('');
  sending = signal(false);
  private linkId: number | null = null;
  savedType = signal<SaleLinkType>('SINGLE');
  private suggestedLineDescription = '';
  private collectionMethod = 'LINK';
  customerOptions = signal<LabeledCustomer[]>([]);
  productOptions = signal<LabeledProduct[]>([]);
  lines = signal<SaleLinkFormLine[]>([]);
  expandedLines = signal<number[]>([]);
  lineSubtotal = signal(0);
  activeTab = signal('generales');
  linkSales = signal<SaleDTO[]>([]);
  salesLoading = signal(false);
  private salesLoaded = false;
  comercialMetadata = signal<Record<string, string>>({});
  linkSubscriptions = signal<SubscriptionDTO[]>([]);
  subscriptionsLoading = signal(false);
  private subscriptionsLoaded = false;
  saleDetailsOpen = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  saleDetailsLoading = signal(false);
  subscriptionDetailsOpen = signal(false);
  selectedSubscription = signal<SubscriptionDTO | null>(null);
  subscriptionDetailsLoading = signal(false);
  customerDetailsOpen = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  customerDetailsLoading = signal(false);
  readonly formatApiDateOnly = formatApiDateOnly;
  customerDialogOpen = signal(false);
  lineDialogOpen = signal(false);
  editingLineIndex = signal<number | null>(null);
  lineFiscalDetailsExpanded = signal(false);
  lineModalShown = signal(false);
  productDialogOpen = signal(false);
  readonly usageHelp =
    '<div class="max-w-xs text-left text-sm leading-snug">' +
    '<p class="mb-2"><span class="font-semibold">Pago único.</span> Se cobra una sola vez. Al pagarse, la liga se cierra.</p>' +
    '<p class="mb-2"><span class="font-semibold">Reutilizable.</span> Se puede cobrar varias veces. Cada pago genera una venta y la liga sigue abierta.</p>' +
    '<p><span class="font-semibold">Suscripción.</span> El primer pago abre una suscripción y los siguientes ciclos usan la frecuencia elegida.</p>' +
    '</div>';
  readonly minExpiresAt = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  readonly defaultExpiresAt = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 0, 0);
  readonly usages = [
    { label: 'Pago único', value: 'SINGLE' },
    { label: 'Reutilizable', value: 'REUSABLE' },
    { label: 'Suscripción', value: 'SUBSCRIPTION' }
  ];
  readonly frequencies = [
    { label: 'Diaria', value: 'DAILY' },
    { label: 'Semanal', value: 'WEEKLY' },
    { label: 'Quincenal', value: 'BIWEEKLY' },
    { label: 'Mensual', value: 'MONTHLY' },
    { label: 'Trimestral', value: 'QUARTERLY' },
    { label: 'Semestral', value: 'SEMIANNUALLY' },
    { label: 'Anual', value: 'ANNUALLY' }
  ];

  form = this.fb.group({
    customer: [null as LabeledCustomer | null],
    lineProduct: [null as LabeledProduct | null],
    lineDescription: ['', [Validators.maxLength(250)]],
    lineQuantity: [1, [Validators.min(0.0001)]],
    lineUnitPrice: [0, [Validators.min(0)]],
    expiresAt: [null as Date | null],
    notes: [''],
    usage: ['SINGLE', Validators.required],
    frequency: [null as string | null]
  });

  constructor() {
    this.customerQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe((query) => {
      this.customerService.listCustomers({
        'legalName[lk]': query,
        'comercialName[lk]': query,
        'taxId[lk]': query
      }, { page: 0, size: 20 }).subscribe((response) => {
        this.customerOptions.set((response.body ?? []).map((customer) => ({
          ...customer,
          displayLabel: customer.comercialName || customer.legalName || customer.taxId
        })));
      });
    });
    this.form.controls.lineProduct.valueChanges.pipe(takeUntilDestroyed()).subscribe((product) => {
      const current = (this.form.controls.lineDescription.value ?? '').trim();
      const name = product?.name?.trim() ?? '';
      if (!current || current === this.suggestedLineDescription) {
        this.suggestedLineDescription = name;
        this.form.controls.lineDescription.setValue(name, { emitEvent: false });
      }
      this.form.controls.lineUnitPrice.setValue(Number(product?.unitPrice ?? 0), { emitEvent: false });
      this.refreshLineSubtotal();
    });
    this.form.controls.lineQuantity.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.refreshLineSubtotal());
    this.form.controls.lineUnitPrice.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.refreshLineSubtotal());
    this.productQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe((query) => {
      this.productService.getProducts({ 'q[lk]': query }, { page: 0, size: 20 }).subscribe((response) => {
        this.productOptions.set((response.body ?? []).map((product) => ({
          ...product,
          displayLabel: product.sku ? `${product.name} (${product.sku})` : product.name
        })));
      });
    });
  }

  ngOnInit(): void {
    const rawId = this.route.snapshot.paramMap.get('id');
    if (rawId) {
      this.editMode.set(true);
      this.linkId = Number(rawId);
      this.loadLink(this.linkId);
    }
  }

  onCustomerSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.customerOptions.set([]);
      return;
    }
    this.customerQuery.next(query);
  }

  onProductSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.productOptions.set([]);
      return;
    }
    this.productQuery.next(query);
  }

  cancel(): void {
    this.router.navigate(['/sale-links']);
  }

  refreshLink(): void {
    const id = this.linkId;
    if (id == null || this.loading()) {
      return;
    }
    this.loadLink(id);
    if (this.activeTab() === 'ventas') {
      this.loadLinkSales();
    }
    if (this.activeTab() === 'suscripciones') {
      this.loadLinkSubscriptions();
    }
  }

  statusLabel(): string {
    const labels: Record<SaleLinkStatus, string> = {
      ACTIVE: 'Activa',
      PAID: 'Pagada',
      CANCELLED: 'Cancelada',
      EXPIRED: 'Vencida'
    };
    const status = this.linkStatus();
    return status ? labels[status] : '—';
  }

  openSendDialog(): void {
    if (this.linkStatus() === 'PAID' || this.linkStatus() === 'CANCELLED') {
      return;
    }
    this.sendEmail.set(this.customerEmail());
    this.sendDialogOpen.set(true);
  }

  submitSend(): void {
    const email = this.sendEmail().trim();
    if (this.linkId == null) {
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.sending.set(true);
    this.saleLinkService.send(this.linkId, email).subscribe({
      next: () => {
        this.sending.set(false);
        this.sendDialogOpen.set(false);
        this.toastr.success('Liga de pago enviada');
      },
      error: (err) => {
        this.sending.set(false);
        this.toastr.error(err?.error?.message ?? 'No se pudo enviar la liga de pago');
      }
    });
  }

  cancelLink(): void {
    if (this.linkId == null || !this.editable()) {
      return;
    }
    this.cancelling.set(true);
    this.saleLinkService.cancel(this.linkId).subscribe({
      next: () => {
        this.toastr.success('Liga cancelada');
        this.router.navigate(['/sale-links']);
      },
      error: () => {
        this.cancelling.set(false);
        this.toastr.error('No se pudo cancelar la liga');
      }
    });
  }

  openPaymentUrl(): void {
    const url = this.paymentUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private loadPaymentUrl(id: number): void {
    this.paymentUrlLoading.set(true);
    this.saleLinkService.publicHash(id).subscribe({
      next: (response) => {
        this.paymentUrlLoading.set(false);
        this.paymentUrl.set(response.body ? this.publicPageUrl(id, response.body) : null);
      },
      error: () => {
        this.paymentUrlLoading.set(false);
        this.paymentUrl.set(null);
      }
    });
  }

  private publicPageUrl(id: number, hash: string): string {
    return this.saleLinkService.publicPageUrl(id, hash, this.savedType());
  }

  private loadLink(id: number): void {
    this.loading.set(true);
    this.saleLinkService.getById(id).subscribe({
      next: (response) => {
        const link = response.body;
        if (!link) {
          this.toastr.error('No se encontró la liga de pago');
          this.router.navigate(['/sale-links']);
          return;
        }
        this.applyLink(link);
        this.loadPaymentUrl(id);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toastr.error('No se pudo cargar la liga de pago');
        this.router.navigate(['/sale-links']);
      }
    });
  }

  private applyLink(link: SaleLinkDTO): void {
    this.linkNumber.set(link.number ?? null);
    this.linkStatus.set(link.status ?? null);
    const type = link.type ?? 'SINGLE';
    this.savedType.set(type);
    if (type === 'SUBSCRIPTION' && this.activeTab() === 'ventas') {
      this.activeTab.set('generales');
    }
    if (type !== 'SUBSCRIPTION' && this.activeTab() === 'suscripciones') {
      this.activeTab.set('generales');
    }
    this.customerEmail.set(link.customerEmail ?? '');
    this.comercialMetadata.set(link.comercialMetadata ?? {});
    this.collectionMethod = link.collectionMethod || 'LINK';
    const active = link.status === 'ACTIVE';
    this.editable.set(active);
    const customerName = link.customerName || link.customerTaxId || '';
    const mapped = (link.lines ?? []).map((line) => {
      const productName = line.productName || '';
      const product = {
        id: line.productId,
        name: productName,
        sku: line.productSku,
        unitPrice: line.unitPrice ?? 0,
        state: 'ACTIVE',
        displayLabel: line.productSku ? `${productName} (${line.productSku})` : productName
      } as LabeledProduct;
      const quantity = line.quantity ?? 1;
      return this.toFormLine(product, quantity, (line.description ?? '').trim() || productName, line.unitPrice ?? 0);
    });
    this.lines.set(mapped);
    this.expandedLines.set([]);
    const productIds = mapped.map((line) => line.product.id).filter((id): id is number => id != null);
    if (productIds.length > 0) {
      forkJoin(productIds.map((id) => this.productService.getProductById(id).pipe(
        map((response) => response.body),
        catchError(() => of(null))
      ))).subscribe((products) => {
        this.lines.update((current) => current.map((line) => {
          const product = products.find((item) => item?.id === line.product.id);
          if (!product) {
            return line;
          }
          const labeled: LabeledProduct = {
            ...product,
            displayLabel: line.product.displayLabel
          };
          return this.toFormLine(labeled, line.quantity, line.description, line.unitPrice);
        }));
      });
    }
    this.form.patchValue({
      customer: link.customerId ? {
        id: link.customerId,
        comercialName: customerName,
        legalName: customerName,
        taxId: link.customerTaxId,
        displayLabel: customerName
      } as LabeledCustomer : null,
      expiresAt: link.expiresAt ? new Date(link.expiresAt) : null,
      notes: link.notes ?? '',
      usage: link.type ?? 'SINGLE',
      frequency: link.frequency ?? null
    });
    if (!active) {
      this.form.disable();
      this.form.controls.notes.enable({ emitEvent: false });
    }
  }

  save(): void {
    const value = this.form.getRawValue();
    if (!value.usage) {
      this.toastr.error('Selecciona el tipo de liga');
      return;
    }
    if (value.usage === 'SUBSCRIPTION' && !value.frequency) {
      this.toastr.error('La suscripción requiere una frecuencia');
      return;
    }
    if (!value.customer?.id) {
      this.toastr.error('Selecciona un cliente o captura uno nuevo');
      return;
    }
    if (value.expiresAt && value.expiresAt.getTime() < Date.now()) {
      this.toastr.error('La vigencia no puede ser una fecha pasada');
      return;
    }
    if (this.lines().length === 0) {
      this.toastr.error('Agrega al menos un producto');
      return;
    }
    if (!this.editable()) {
      return;
    }
    const request = {
      customerId: value.customer?.id,
      lines: this.lines().map((line) => ({
        productId: line.product.id,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        description: line.description || line.product.name
      })),
      expiresAt: value.expiresAt ? value.expiresAt.toISOString() : undefined,
      notes: value.notes || undefined,
      comercialMetadata: this.editMode() ? this.comercialMetadata() : undefined,
      type: value.usage as SaleLinkType,
      frequency: value.usage === 'SUBSCRIPTION' ? value.frequency as never : undefined,
      collectionMethod: this.collectionMethod
    };
    this.saving.set(true);
    const request$ = this.editMode() && this.linkId != null
      ? this.saleLinkService.update(this.linkId, request)
      : this.saleLinkService.create(request);
    request$.subscribe({
      next: () => {
        this.toastr.success(this.editMode() ? 'Liga de pago actualizada' : 'Liga de pago creada');
        this.router.navigate(['/sale-links']);
      },
      error: () => {
        this.saving.set(false);
        this.toastr.error(this.editMode() ? 'No se pudo actualizar la liga de pago' : 'No se pudo crear la liga de pago');
      }
    });
  }

  showSalesTab(): boolean {
    return this.editMode() && this.savedType() !== 'SUBSCRIPTION';
  }

  showSubscriptionsTab(): boolean {
    return this.editMode() && this.savedType() === 'SUBSCRIPTION';
  }

  showMetadataTab(): boolean {
    return this.editMode();
  }

  metadataRows(): { key: string; value: string }[] {
    return Object.entries(this.comercialMetadata()).map(([key, value]) => ({ key, value }));
  }

  onTabChange(value: string | number | undefined): void {
    const tab = String(value ?? 'generales');
    if (tab === 'ventas' && !this.showSalesTab()) {
      return;
    }
    if (tab === 'suscripciones' && !this.showSubscriptionsTab()) {
      return;
    }
    this.activeTab.set(tab);
    if (tab === 'ventas' && !this.salesLoaded) {
      this.loadLinkSales();
    }
    if (tab === 'suscripciones' && !this.subscriptionsLoaded) {
      this.loadLinkSubscriptions();
    }
  }

  loadLinkSales(): void {
    if (this.linkId == null) {
      return;
    }
    this.salesLoading.set(true);
    this.saleService.searchSales({ saleLinkId: this.linkId }, { page: 0, size: 100, sort: ['createdAt,desc'] }).pipe(
      catchError(() => of({ ok: false, code: 0, message: '', body: [] as SaleDTO[] })),
      finalize(() => this.salesLoading.set(false))
    ).subscribe((response) => {
      this.linkSales.set(response.ok && response.body ? response.body : []);
      this.salesLoaded = true;
    });
  }

  loadLinkSubscriptions(): void {
    if (this.linkId == null) {
      return;
    }
    this.subscriptionsLoading.set(true);
    this.subscriptionService.searchSubscriptions(
      { saleLinkId: this.linkId },
      { page: 0, size: 100, sort: ['createdAt,desc'] }
    ).pipe(
      catchError(() => of({ ok: false, code: 0, message: '', body: [] as SubscriptionDTO[] })),
      finalize(() => this.subscriptionsLoading.set(false))
    ).subscribe((response) => {
      this.linkSubscriptions.set(response.ok && response.body ? response.body : []);
      this.subscriptionsLoaded = true;
    });
  }

  openSaleDetails(saleId: number | undefined): void {
    if (saleId == null) {
      return;
    }
    this.saleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          if (detail.id != null) {
            this.saleService.getSaleCharges(detail.id).subscribe({
              next: (chargesResp) => this.saleCharges.set(chargesResp.body ?? []),
              error: () => this.saleCharges.set([])
            });
          }
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('No se pudo cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.saleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeSaleDetails();
    }
  }

  openSubscriptionDetails(subscriptionId: number | undefined): void {
    if (subscriptionId == null) {
      return;
    }
    this.subscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: () => {
        this.subscriptionDetailsLoading.set(false);
        this.toastr.error('No se pudo cargar el detalle de la suscripción');
        this.closeSubscriptionDetails();
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.subscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeSubscriptionDetails();
    }
  }

  openCustomerDetails(customerId: number | undefined): void {
    if (customerId == null) {
      return;
    }
    this.customerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('No se pudo cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.customerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeCustomerDetails();
    }
  }

  subscriptionStatusLabel(status: string | undefined): string {
    const labels: Record<string, string> = {
      PENDING_PAYMENT: 'Pendiente de pago',
      TRAILING: 'Periodo de prueba',
      ACTIVE: 'Activa',
      PAUSED: 'Pausada',
      PAST_DUE: 'Vencida de pago',
      CANCELED: 'Cancelada',
      CANCELLED: 'Cancelada',
      FINISHED: 'Finalizada',
      EXPIRED: 'Expirada'
    };
    return status ? labels[status] ?? status : '—';
  }

  subscriptionStatusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'PENDING_PAYMENT' || status === 'TRAILING' || status === 'PAST_DUE') return 'warn';
    if (status === 'PAUSED') return 'info';
    if (status === 'CANCELED' || status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  subscriptionStatusIcon(status: string | undefined): string {
    if (status === 'PENDING_PAYMENT' || status === 'TRAILING') return 'pi pi-clock';
    if (status === 'ACTIVE') return 'pi pi-check-circle';
    if (status === 'PAUSED') return 'pi pi-pause';
    if (status === 'PAST_DUE') return 'pi pi-exclamation-triangle';
    if (status === 'CANCELED' || status === 'CANCELLED') return 'pi pi-ban';
    if (status === 'FINISHED') return 'pi pi-flag';
    if (status === 'EXPIRED') return 'pi pi-calendar-times';
    return 'pi pi-sync';
  }

  subscriptionStatusIconClasses(status: string | undefined): string {
    const base = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full';
    if (status === 'PENDING_PAYMENT' || status === 'TRAILING') return `${base} bg-amber-100 text-amber-600`;
    if (status === 'ACTIVE') return `${base} bg-emerald-100 text-emerald-600`;
    if (status === 'PAUSED') return `${base} bg-sky-100 text-sky-600`;
    if (status === 'PAST_DUE') return `${base} bg-orange-100 text-orange-600`;
    if (status === 'CANCELED' || status === 'CANCELLED') return `${base} bg-rose-100 text-rose-600`;
    if (status === 'EXPIRED' || status === 'FINISHED') return `${base} bg-slate-200 text-slate-600`;
    return `${base} bg-slate-100 text-slate-600`;
  }

  subscriptionFrequencyLabel(frequency: string | undefined): string {
    return this.frequencies.find((item) => item.value === frequency)?.label ?? (frequency || '—');
  }

  saleCollectionDueDate(sale: SaleDTO): Date | null {
    const start = parseApiCalendarDate(sale.collectionStartDate)?.toJSDate() ?? null;
    if (!start) {
      return null;
    }
    const days = sale.daysUntilExpiry != null && Number.isFinite(Number(sale.daysUntilExpiry))
      ? Math.trunc(Number(sale.daysUntilExpiry))
      : 0;
    const due = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    due.setDate(due.getDate() + days);
    return due;
  }

  isDueDateExpired(due: Date | null): boolean {
    if (!due) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
  }

  saleStatusLabel(status: string | undefined): string {
    const labels: Record<string, string> = {
      DRAFT: 'Borrador',
      ISSUED: 'Emitida',
      PARTIALLY_PAID: 'Parcial',
      PAID: 'Pagada',
      VOID: 'Anulada',
      UNCOLLECTIBLE: 'Incobrable'
    };
    return status ? labels[status] ?? status : '—';
  }

  saleStatusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'PAID') return 'success';
    if (status === 'ISSUED' || status === 'PARTIALLY_PAID') return 'warn';
    if (status === 'VOID' || status === 'UNCOLLECTIBLE') return 'danger';
    if (status === 'DRAFT') return 'info';
    return 'secondary';
  }

  saleStatusIcon(status: string | undefined): string {
    if (status === 'DRAFT') return 'pi pi-file-edit';
    if (status === 'ISSUED') return 'pi pi-send';
    if (status === 'PARTIALLY_PAID') return 'pi pi-wallet';
    if (status === 'PAID') return 'pi pi-check-circle';
    if (status === 'VOID') return 'pi pi-ban';
    if (status === 'UNCOLLECTIBLE') return 'pi pi-exclamation-triangle';
    return 'pi pi-receipt';
  }

  saleStatusIconClasses(status: string | undefined): string {
    const base = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full';
    if (status === 'DRAFT') return `${base} bg-sky-100 text-sky-600`;
    if (status === 'ISSUED') return `${base} bg-amber-100 text-amber-600`;
    if (status === 'PARTIALLY_PAID') return `${base} bg-violet-100 text-violet-600`;
    if (status === 'PAID') return `${base} bg-green-100 text-green-600`;
    if (status === 'VOID') return `${base} bg-slate-100 text-slate-500`;
    if (status === 'UNCOLLECTIBLE') return `${base} bg-red-100 text-red-600`;
    return `${base} bg-slate-100 text-slate-600`;
  }

  openCustomerDialog(): void {
    if (!this.editable()) {
      return;
    }
    this.customerDialogOpen.set(true);
  }

  openLineDialog(index?: number): void {
    if (!this.editable()) {
      return;
    }
    this.lineFiscalDetailsExpanded.set(false);
    this.editingLineIndex.set(index ?? null);
    if (index == null) {
      this.suggestedLineDescription = '';
      this.form.controls.lineDescription.setValue('');
      this.form.controls.lineProduct.setValue(null);
      this.form.controls.lineQuantity.setValue(1);
      this.form.controls.lineUnitPrice.setValue(0);
    } else {
      const line = this.lines()[index];
      if (!line) {
        return;
      }
      const productName = line.product.name?.trim() ?? '';
      this.suggestedLineDescription = productName;
      this.form.controls.lineDescription.setValue(line.description);
      this.form.controls.lineProduct.setValue(line.product);
      this.form.controls.lineQuantity.setValue(line.quantity);
      this.form.controls.lineUnitPrice.setValue(line.unitPrice);
    }
    this.refreshLineSubtotal();
    this.lineDialogOpen.set(true);
    setTimeout(() => this.lineModalShown.set(true), 10);
    setTimeout(() => this.focusLineProduct(), 100);
  }

  private focusLineProduct(retry = true): void {
    const input = document.getElementById('lineProduct');
    if (input instanceof HTMLInputElement) {
      input.focus();
    } else if (retry) {
      setTimeout(() => this.focusLineProduct(false), 250);
    }
  }

  closeLineDialog(): void {
    this.lineModalShown.set(false);
    setTimeout(() => {
      this.lineDialogOpen.set(false);
      this.editingLineIndex.set(null);
    }, 200);
  }

  openProductDialog(): void {
    if (!this.editable()) {
      return;
    }
    this.productDialogOpen.set(true);
  }

  onCustomerSaved(customer: CustomerDTO): void {
    if (customer.id == null) {
      return;
    }
    const name = customer.comercialName || customer.legalName || customer.taxId || '';
    this.form.controls.customer.setValue({
      ...customer,
      displayLabel: name
    });
    this.customerDialogOpen.set(false);
  }

  private refreshLineSubtotal(): void {
    const quantity = Number(this.form.controls.lineQuantity.value ?? 0);
    const unitPrice = Number(this.form.controls.lineUnitPrice.value ?? 0);
    this.lineSubtotal.set(quantity * unitPrice);
  }

  addLine(): void {
    const product = this.form.controls.lineProduct.value;
    const quantity = this.form.controls.lineQuantity.value ?? 1;
    if (!product?.id) {
      this.toastr.error('Selecciona un producto');
      return;
    }
    if (quantity <= 0) {
      this.toastr.error('La cantidad debe ser mayor a cero');
      return;
    }
    const unitPrice = Number(this.form.controls.lineUnitPrice.value ?? 0);
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      this.toastr.error('El precio unitario no puede ser negativo');
      return;
    }
    const typed = (this.form.controls.lineDescription.value ?? '').trim();
    if (typed.length > 250) {
      this.toastr.error('La descripción no puede exceder 250 caracteres');
      return;
    }
    const description = typed || product.name || '';
    const formLine = this.toFormLine(product, quantity, description, unitPrice);
    const index = this.editingLineIndex();
    if (index == null) {
      this.lines.update((current) => [...current, formLine]);
    } else {
      this.lines.update((current) => current.map((line, i) => i === index ? formLine : line));
    }
    this.suggestedLineDescription = '';
    this.form.controls.lineDescription.setValue('');
    this.form.controls.lineProduct.setValue(null);
    this.form.controls.lineQuantity.setValue(1);
    this.closeLineDialog();
  }

  removeLine(index: number): void {
    this.lines.update((current) => current.filter((_, i) => i !== index));
    this.expandedLines.update((current) => current.filter((i) => i !== index).map((i) => i > index ? i - 1 : i));
  }

  toggleLineExpanded(index: number): void {
    this.expandedLines.update((current) => current.includes(index)
      ? current.filter((i) => i !== index)
      : [...current, index]);
  }

  isLineExpanded(index: number): boolean {
    return this.expandedLines().includes(index);
  }

  private toFormLine(product: LabeledProduct, quantity: number, description: string, unitPrice: number): SaleLinkFormLine {
    return {
      product,
      quantity,
      description,
      ...this.quoteAmounts(product, quantity, unitPrice)
    };
  }

  private quoteAmounts(product: LabeledProduct, quantity: number, unitPrice: number): Pick<SaleLinkFormLine, 'unitPrice' | 'subtotal' | 'taxAmount' | 'totalAmount' | 'taxes'> {
    const gross = unitPrice * quantity;
    const inclusive = product.taxInclusive === true;
    const active = (product.taxes ?? []).filter((tax) => tax.state !== 'INACTIVE' && tax.applicationType !== 'EXEMPT');
    const decimalRate = (rate: number | undefined) => {
      const value = Number(rate ?? 0);
      if (value <= 0) {
        return 0;
      }
      return value <= 1 ? value : value / 100;
    };
    if (active.length === 0) {
      const amount = this.round4(gross);
      return { unitPrice, subtotal: amount, taxAmount: 0, totalAmount: amount, taxes: [] };
    }
    if (inclusive) {
      const combined = active.reduce((sum, tax) => sum + decimalRate(tax.taxRate), 0);
      const base = gross / (1 + combined);
      const taxes = active.map((tax) => ({
        taxCode: tax.tax?.taxKey,
        taxRate: tax.taxRate,
        taxBase: base,
        taxAmount: base * decimalRate(tax.taxRate),
        collectionType: tax.collectionType
      }));
      const taxAmount = this.round4(gross - base);
      const totalAmount = this.round4(gross);
      return { unitPrice, subtotal: this.round4(totalAmount - taxAmount), taxAmount, totalAmount, taxes };
    }
    const taxes = active.map((tax) => ({
      taxCode: tax.tax?.taxKey,
      taxRate: tax.taxRate,
      taxBase: gross,
      taxAmount: gross * decimalRate(tax.taxRate),
      collectionType: tax.collectionType
    }));
    const taxAmount = this.round4(taxes.reduce((sum, tax) => sum + (tax.taxAmount ?? 0), 0));
    const subtotal = this.round4(gross);
    return { unitPrice, subtotal, taxAmount, totalAmount: this.round4(subtotal + taxAmount), taxes };
  }

  private round4(value: number): number {
    return Math.round((value + Number.EPSILON) * 10000) / 10000;
  }

  fiscalMeasureUnit(): string {
    const product = this.form.controls.lineProduct.value;
    const code = product?.measureUnitCode?.trim();
    const name = product?.measureUnitName?.trim();
    if (code && name) {
      return `${code} - ${name}`;
    }
    return name || code || '—';
  }

  fiscalTaxCode(): string {
    const product = this.form.controls.lineProduct.value;
    const code = product?.taxCodeTaxCode?.trim();
    const description = product?.taxCodeDescription?.trim();
    if (code && description) {
      return `${code} - ${description}`;
    }
    return description || code || '—';
  }

  fiscalObjetoImp(): string {
    const product = this.form.controls.lineProduct.value;
    const id = product?.satObjetoImpId?.trim();
    const name = product?.satObjetoImpName?.trim();
    if (id && name) {
      return `${id} - ${name}`;
    }
    return name || id || '—';
  }

  fiscalTaxes() {
    return (this.form.controls.lineProduct.value?.taxes ?? []).filter((tax) => tax.state !== 'INACTIVE');
  }

  taxLabel(tax: { taxKey?: string; displayName?: string } | undefined): string {
    if (!tax?.taxKey && !tax?.displayName) {
      return '—';
    }
    if (tax.taxKey && tax.displayName) {
      return `${tax.taxKey} - ${tax.displayName}`;
    }
    return tax.displayName || tax.taxKey || '—';
  }

  taxApplicationLabel(type: string | undefined): string {
    switch (type) {
      case 'TAXABLE':
        return 'Gravado';
      case 'EXEMPT':
        return 'Exento';
      case 'ZERO_RATED':
        return 'Tasa 0%';
      case 'REVERSE_CHARGE':
        return 'Inversión del sujeto';
      default:
        return type || '—';
    }
  }

  taxCollectionLabel(type: string | undefined): string {
    switch (type) {
      case 'SELLER_COLLECTS':
        return 'Traslado';
      case 'SELLER_WITHHOLDS':
        return 'Retención';
      default:
        return '—';
    }
  }

  formatTaxRate(rate: number | undefined | null): string {
    if (rate == null) {
      return '—';
    }
    if (rate > 0 && rate <= 1) {
      return `${(rate * 100).toFixed(2)}%`;
    }
    return `${Number(rate).toFixed(2)}%`;
  }

  onProductSaved(product: ProductDTO): void {
    if (product.id == null) {
      return;
    }
    const name = product.name || '';
    const labeled = {
      ...product,
      displayLabel: product.sku ? `${name} (${product.sku})` : name
    };
    this.form.controls.lineProduct.setValue(labeled);
    this.productDialogOpen.set(false);
  }
}
